import React, { useState, useEffect } from 'react';
import { Table, Button, Spinner, Badge, Form, InputGroup, Pagination, Dropdown, Row, Col, Modal, Alert } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter, FaEllipsisV, FaCheckCircle, FaTimesCircle, FaEye, FaUserPlus, 
         FaUser, FaEnvelope, FaPhone, FaPlus, FaChartLine, FaCreditCard, FaHome, FaMoneyBillWave,
         FaFileUpload, FaDownload } from 'react-icons/fa';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { rmApi, productApi } from '../../services/api';
import './Leads.css';

// Case status mapping
const caseStatusMap = {
  1: { label: 'Fresh', variant: 'primary' },
  11: { label: 'Rejected', variant: 'danger' },
  13: { label: 'Disbursed', variant: 'success' },
  14: { label: 'Not Interested', variant: 'danger' },
};

// Case type mapping
const caseTypeMap = {
  'Credit Card': 'Credit Card',
  'Home Loan': 'Home Loan',
  'Personal Loan': 'Personal Loan',
  'Business Loan': 'Business Loan'
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate}, ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

function RMLeads({ onLeadsUpdate }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [
    { value: 5, label: '5 rows' },
    { value: 10, label: '10 rows' },
    { value: 25, label: '25 rows' },
    { value: 50, label: '50 rows' },
  ];
  
  // Filter state
  const [products, setProducts] = useState([]);
  const [dsos, setDsos] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingDsos, setLoadingDsos] = useState(false);
  const [productFilter, setProductFilter] = useState(null);
  const [dsoFilter, setDsoFilter] = useState(null);
  const [addLeadError, setAddLeadError] = useState('');
  const [addLeadLoading, setAddLeadLoading] = useState(false);
  
  // Lead status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: '1', label: 'Fresh' },
    { value: '11', label: 'Rejected' },
    { value: '13', label: 'Disbursed' },
    { value: '14', label: 'Not Interested' },
  ];
  

  useEffect(() => {
    fetchLeads();
    fetchProducts(); 
    fetchDsos();
  }, [searchParams, currentPage, pageSize, searchTerm, statusFilter, productFilter, dsoFilter]);
  
  // Fetch DSOs for the filter dropdown
  const fetchDsos = async () => {
    setLoadingDsos(true);
    try {
      const response = await rmApi.getAssignedDsos({
        limit: 100 // Get a larger number for dropdown
      });
      console.log('DSOs fetched:', response.data);
      setDsos(response.data.items || []);
    } catch (err) {
      console.error('Error fetching DSOs:', err);
    } finally {
      setLoadingDsos(false);
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    
    // Build query parameters
    const params = {
      skip: (currentPage - 1) * pageSize,
      limit: pageSize,
    };
    
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    if (statusFilter) {
      params.status = statusFilter;
    }
    
    // Add product filter if selected
    if (productFilter && productFilter.value) {
      params.product_id = productFilter.value;
    }
    
    // Add DSO filter if selected
    if (dsoFilter && dsoFilter.value) {
      params.dso_id = dsoFilter.value;
      console.log('Applying DSO filter with ID:', dsoFilter.value);
    }
    
    try {
      const response = await rmApi.getLeads(params);
      console.log('Leads fetched:', response.data);
      // Log the first lead's structure to see the exact DSO ID field
      if (Array.isArray(response.data) && response.data.length > 0) {
        console.log('Lead sample structure:', response.data[0]);
      }
      
      // Check if response.data is an array directly (typical for this API)
      if (Array.isArray(response.data)) {
        setLeads(response.data);
        setTotalLeads(response.data.length);
      } 
      // Otherwise check if it has an 'items' property
      else if (response.data && response.data.items) {
        setLeads(response.data.items);
        setTotalLeads(response.data.total || response.data.items.length);
      }
      // If neither format works, set empty array
      else {
        console.error('Unexpected data format:', response.data);
        setLeads([]);
        setTotalLeads(0);
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to load leads. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // fetchLeads will be triggered by the effect hook when currentPage changes
  };

  const handleFilterChange = (filterType, selectedOption) => {
    if (filterType === 'status') {
      const value = selectedOption ? selectedOption.value : '';
      setStatusFilter(value);
    } else if (filterType === 'product') {
      setProductFilter(selectedOption);
    } else if (filterType === 'dso') {
      setDsoFilter(selectedOption);
    }
    
    // Reset to first page when changing filters
    setCurrentPage(1);
    // fetchLeads will be triggered by the effect hook when the dependencies change
  };
  
  // Clear all filters
  const clearFilters = () => {
    setStatusFilter('');
    setProductFilter(null);
    setDsoFilter(null);
    setCurrentPage(1);
  };
  
  // Function to manually trigger a refresh of the leads data
  const refreshLeads = () => {
    fetchLeads();
  };
  
  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await productApi.getProducts();
      setProducts(response.data.items || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };
  

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (selectedOption) => {
    const newPageSize = selectedOption.value;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    // fetchLeads will be triggered by the effect hook
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalLeads / pageSize);
    if (totalPages <= 1 && totalLeads <= pageSize) return null;

    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalLeads);

    const pageButtons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(startPage + maxButtons - 1, totalPages);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      pageButtons.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
          className={number === currentPage ? 'active' : ''}
        >
          {number}
        </Pagination.Item>
      );
    }

    return (
      <div className="dso-pagination-container">
        <div className="dso-pagination-info-bar">
          <div className="dso-pagination-info">
            Showing {startRecord} to {endRecord} of {totalLeads} records
          </div>
          
          <div className="dso-pagination-pagesize">
            <span className="me-2">Rows per page:</span>
            <Select
              className="dso-pagesize-select"
              classNamePrefix="react-select"
              options={pageSizeOptions}
              value={pageSizeOptions.find(option => option.value === pageSize)}
              onChange={handlePageSizeChange}
              isSearchable={false}
              menuPlacement="top"
            />
          </div>
        </div>
        
        <div className="dso-pagination-controls">
          <Pagination>
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
              className="pagination-first"
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              className="pagination-prev"
            />
            
            {startPage > 1 && (
              <>
                <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                {startPage > 2 && <Pagination.Ellipsis />}
              </>
            )}
            
            {pageButtons}
            
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <Pagination.Ellipsis />}
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              </>
            )}
            
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="pagination-next"
            />
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
              className="pagination-last"
            />
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="dso-leads-container">
        <div className="dso-leads-header">
          <h3>Leads</h3>
      </div>

      {/* Search Row */}
      <Row className="dso-leads-filters-row mb-3">
        <Col xs={12}>
          <Form onSubmit={handleSearch} className="dso-search-form w-100">
            <InputGroup size="sm">
              <Form.Control
                placeholder="Search name or phone #"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary" type="submit">
                <FaSearch />
              </Button>
            </InputGroup>
          </Form>
        </Col>
      </Row>
      
      {/* Filters Row */}
      <Row className="dso-leads-filters-row mb-3">
        <Col xs={12} className="d-flex flex-wrap align-items-center gap-2">
          <div className="filter-container">
            <Select
              placeholder="Filter by Product"
              isClearable
              isLoading={loadingProducts}
              options={products.map(p => ({ value: p.id, label: p.name }))}
              value={productFilter}
              onChange={(option) => handleFilterChange('product', option)}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          
          <div className="filter-container">
            <Select
              placeholder="Filter by DSO"
              isClearable
              isLoading={loadingDsos}
              options={dsos.map(dso => ({ 
                value: dso.id, 
                label: dso.name 
              }))}
              value={dsoFilter}
              onChange={(option) => handleFilterChange('dso', option)}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          
          <div className="filter-container">
            <Select
              placeholder="Filter by Status"
              isClearable
              options={statusOptions}
              value={statusOptions.find(option => option.value === statusFilter) || null}
              onChange={(option) => handleFilterChange('status', option)}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          
          <Button 
            variant="outline-secondary"
            onClick={clearFilters}
            title="Clear all filters"
            className="d-flex align-items-center gap-1"
          >
            <FaFilter /> Clear
          </Button>
        </Col>
      </Row>

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="dso-leads-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {leads.length === 0 ? (
            <div className="dso-leads-empty">
              <p>No leads found with the current filters.</p>
            </div>
          ) : (
            <>
              <div className="dso-leads-table-container">
                <Table responsive striped className="dso-leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Product</th>
                      <th>DSO</th>
                      <th>Agent</th>
                      <th>Status</th>
                      <th>Application #</th>
                      <th>Created On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => (
                      <tr key={lead.id}>
                        <td>{lead.user_name}</td>
                        <td>{lead.user_mobile}</td>
                        <td>
                          <div className="dso-lead-product">
                            <span className="dso-lead-product-name">{lead.product_name}</span>
                            <span className="dso-lead-product-type">{caseTypeMap[lead.case_type] || lead.case_type}</span>
                          </div>
                        </td>
                        <td>{lead.dso_name}</td>
                        <td>{lead.agent_name}</td>
                        <td>
                          <Badge 
                            bg={caseStatusMap[lead.case_status]?.variant || 'secondary'}
                            className="dso-lead-status"
                          >
                            {lead.case_status_str || 'Unknown'}
                          </Badge>
                        </td>
                        <td>
                          {lead.application_no || 'N/A'}
                        </td>
                        <td>{formatDate(lead.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              {renderPagination()}
            </>
          )}
        </>
      )}
    </div>
    </React.Fragment>
  );
}

export default RMLeads;
